import { random } from 'remotion';

// Types
import type { ConfettiColor, ConfettiConfig } from './types';

// Constants
import { DEFAULT_CONFETTI_CONFIG } from './constants';

/**
 * Gets a config property value with fallback to default
 */
export const getConfigValue = <K extends keyof ConfettiConfig>(
  options: ConfettiConfig,
  key: K,
): NonNullable<ConfettiConfig[K]> => {
  const value = options[key];

  return (value ?? DEFAULT_CONFETTI_CONFIG[key]) as NonNullable<
    ConfettiConfig[K]
  >;
};

/**
 * Ensures a number is a positive integer (or zero)
 */
export const toPositiveInt = (value: number): number =>
  Math.max(0, Math.floor(value));

/**
 * Generates a deterministic random integer within range [min, max)
 */
export const randomInt = (min: number, max: number): number =>
  Math.floor(random('int') * (max - min)) + min;

/**
 * Converts a hex color string to RGB components
 */
export const hexToRgb = (hex: string): ConfettiColor => {
  // Remove non-hex characters and normalize to 6 digits
  const cleaned = hex.replace(/[^0-9a-f]/gi, '');
  const normalized =
    cleaned.length < 6
      ? cleaned
          .slice(0, 3)
          .split('')
          .map((c) => c + c)
          .join('')
      : cleaned;

  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
};

/**
 * Converts an array of hex color strings to RGB objects
 */
export const colorsToRgb = (colors: string[]): ConfettiColor[] =>
  colors.map(hexToRgb);
